<template>
  <v-card class="pa-0 beneficiary-card">
    <!-- current message dialog is custom so onexdialog cannot be used readily -->
    <MessageDialog
      :show-dialog="showMessageDialog"
      :has-button-two="true"
      type="warning"
      :title="$t('L_cancel_changes')"
      :message="$t('L_by_cancelling_no_change')"
      :button-two-label="$t('L_yes_cancel')"
      :button-one-label="$t('L_no_continue')"
      :is-persistent="true"
      @buttonTwoAction="closeThisPage"
      @buttonOneAction="showMessageDialog = false"
    />
    <!-- Beneficiary Details -->
    <v-card-title>
      <v-col>
        <h1 role="heading" aria-level="2" class="text-h1">
          {{ $t('L_Beneficiary_Details') }}
        </h1>
      </v-col>
    </v-card-title>

    <v-divider />

    <v-card-text>
      <!-- Enter information for <Brenda J Hayes's> <primary> beneficiary -->
      <v-row>
        <v-col cols="12">
          <span>
            {{ enterInfoText }}
          </span>
        </v-col>
      </v-row>

      <!-- Required fields -->
      <v-row>
        <v-col cols="12" class="pt-0 pb-0">
          <p><sup class="redText">*</sup> {{ $t('L_Required_Fields') }}</p>
        </v-col>
      </v-row>

      <!-- Beneficiary Type Options -->
      <v-row class="mb-2">
        <v-col cols="12" sm="7" class="pt-0 pb-0">
          <OnexSelect
            :items="beneficiaryTypeList"
            :placeholder="$t('L_Beneficiary_Type')"
            v-model="beneficiaryType"
            :label="$t('L_Beneficiary_Type')"
            required
            :menu-props="{ location: 'bottom center', scrollStrategy: 'close' }"
          />
        </v-col>
      </v-row>

      <!-- Inputs -->
      <v-form id="form-beneficiary" ref="form" v-model="validForm">
        <v-row v-if="beneficiaryType !== ''">
          <!-- Allocation % - Input -->
          <v-col
            cols="12"
            sm="7"
            v-if="beneficiaryType === 'Y' || beneficiaryType === 'X'"
            class="pt-0 pb-0 mt-n2"
          >
            <OnexAllocation
              data-comp-id="allocation-input"
              v-model="allocationPercentage"
              :label="$t('L_allocation_percentage')"
              required
              :allowOnlyNumeric="true"
              inputmode="decimal"
            >
              <template #append-inner>
                <v-tooltip
                  interactive
                  :contained="true"
                  location="top"
                  :open-on-hover="false"
                  :open-on-click="true"
                  width="240"
                  max-width="240"
                  max-height="60"
                >
                  <template #activator="{ props: activatorProps }">
                    <OnexButton
                      color="primary"
                      :icon="mdiInformation"
                      size="small"
                      variant="text"
                      v-bind="activatorProps"
                    />
                  </template>
                  <div>{{ $t('L_allocation_percentage_tooltip') }}</div>
                </v-tooltip>
              </template>
            </OnexAllocation>
          </v-col>
          <!-- Allocation % - Label -->
          <v-col
            cols="12"
            sm="7"
            v-if="beneficiaryType === 'C' || beneficiaryType === 'E'"
            class="pt-0 pb-0"
          >
            <span>{{ $t('L_allocation_percentage') }} </span>
          </v-col>
          <v-spacer></v-spacer>
          <!-- Allocation %  -->
          <v-col
            cols="12"
            sm="7"
            v-if="beneficiaryType === 'C' || beneficiaryType === 'E'"
            class="pt-0 pb-0 mb-6"
          >
            {{ allocationPercentage }}
          </v-col>
          <!-- First Name - Input -->
          <!-- <v-col mb-4 font-size--12 v-else-if="(beneficiaryType ==='X' || beneficiaryType === 'Y') && $vuetify.display.xs">
            {{ $t('L_allocation_percentage_tooltip') }}
          </v-col> -->
          <v-col cols="12" sm="7" v-if="showName" class="pt-0 pb-0 mt-n2">
            <OnexFirstName
              data-comp-id="first-name"
              v-model="firstName"
              input-id="first_name"
              :auto-capitalize="true"
              :max-length="50"
              :component-validations="[v$.firstName.firstNameValidations.$response]"
              required
            />
          </v-col>
          <!-- Middle Name - Input -->
          <v-col cols="12" sm="7" v-if="showName" class="pt-0 pb-0 mt-n2">
            <OnexMiddleName
              data-comp-id="middle-name"
              v-model="middleName"
              :label="$t('L_Middle_Name')"
              :auto-capitalize="true"
              :max-length="50"
              :component-validations="[v$.middleName.middleNameValidations.$response]"
            />
          </v-col>
          <!-- Last Name - Input -->
          <v-col cols="12" sm="7" v-if="showName" class="pt-0 pb-0 mt-n2">
            <OnexLastName
              data-comp-id="last_name"
              v-model="lastName"
              :label="$t('L_Last_Name')"
              :auto-capitalize="true"
              :max-length="50"
              :component-validations="[v$.lastName.lastNameValidations.$response]"
              required
            />
          </v-col>

          <!-- Full Name - Input -->
          <v-col cols="12" sm="7" v-if="showFullName" class="pt-0 pb-0 mt-n2">
            <OnexText
              :label="$t('L_full_name')"
              v-model="fullName"
              id="full_name"
              data-comp-id="full_name"
              :placeholder="$t('L_full_name')"
              :error-messages="errorMessagesFullName ? errorMessagesFullName : null"
              @blur="onBlurFullName"
              @change="onBlurFullName"
              @keyup="onBlurFullName"
              :max-length="75"
              required
            >
              <template #append-inner>
                <v-icon
                  class="right-icon"
                  v-show="fullNameDirty && errorMessagesFullName === ''"
                  color="success"
                  :icon="mdiCheckCircle"
                />
                <v-icon
                  class="right-icon"
                  v-show="errorMessagesFullName && errorMessagesFullName.length > 0"
                  color="error"
                  :icon="mdiAlertCircle"
                />
              </template>
            </OnexText>
          </v-col>
          <v-col cols="12" sm="7" v-if="showSSNorSIN" class="pt-0 pb-0 mt-n2">
            <OnexSsn
              data-comp-id="ssn"
              :format="isCanadaPolicy !== 'ca' ? SSNFormat.SSN : SSNFormat.SIN"
              allow-sin-business
              allow-sin-itn
              v-model="ssn"
              :label="getSSNLabel"
              sensitive
            />
          </v-col>

          <!-- Relationship to Insured - Input -->
          <v-col cols="12" sm="7" v-if="showRelationshipToInsured" class="pt-0 pb-0 mt-n2">
            <OnexSelect
              :label="$t('L_Relationship_to_Insured')"
              data-comp-id="relationship_to_insured"
              :items="relationshipToInsuredList"
              :placeholder="$t('L_Relationship_to_Insured')"
              v-model="relationshipToInsured"
              required
              :menu-props="{ location: 'bottom center', scrollStrategy: 'close' }"
            />
          </v-col>

          <v-col
            cols="12"
            sm="7"
            v-if="showRevocableCheckbox"
            class="mt-0 font-size--12 pt-0 pb-0 mt-n2"
          >
            <div class="font-weight-bold">
              {{ $t('L_if_you_are_a_resident_of_quebec') }}
            </div>
            <v-checkbox v-model="revocable" color="primary" :label="$t('L_revocable')" />
          </v-col>
          <v-col
            cols="12"
            sm="7"
            v-if="showBeneficiaryUnderAgeOf18AndTrustee"
            class="mt-4 font-size--12 pt-0 pb-0 mt-n2"
          >
            <OnexRadioGroup
              id="beneficiary-under18"
              data-comp-id="beneficiary-under18"
              v-model="beneficiaryUnder18"
              :mandatory="true"
              :rules="[(v: any) => !!v]"
              :label="$t('L_Beneficiary_under_age_of_18')"
              :required-msg-override="$t('L_Relationship_to_Insured_is_required')"
              required
              class="mt-n2"
            >
              <OnexRadio
                v-for="(option, index) in beneficiaryUnder18YesAndNo"
                :id="option.id || 'radio_input_' + index"
                :key="option.name"
                :label="option.label"
                :value="option.value"
                required
              >
                <template #label>
                  <span :class="$vuetify.display.smAndUp ? 'font-size--16' : 'font-size--14'">
                    {{ $t(`${option.label}`) }}
                  </span>
                </template>
              </OnexRadio>
            </OnexRadioGroup>
          </v-col>
          <v-col
            cols="12"
            sm="7"
            class="pt-0 pb-0 mt-n2"
            v-if="showBeneficiaryUnderAgeOf18AndTrustee && beneficiaryUnder18 === 'Y'"
          >
            <OnexText
              v-model="trusteeFirstName"
              id="trusteeFirstName"
              data-comp-id="trusteeFirstName"
              :error-messages="errorMessagesTrusteeFirstName ? errorMessagesTrusteeFirstName : null"
              @blur="onBlurTrusteeFirstName"
              @change="onBlurTrusteeFirstName"
              @keyup="onBlurTrusteeFirstName"
              :label="$t('L_trustee_first_name')"
              :max-length="50"
              required
            >
              <template #append-inner>
                <v-icon
                  class="right-icon"
                  v-show="trusteeFirstNameDirty && errorMessagesTrusteeFirstName === ''"
                  color="success"
                  :icon="mdiCheckCircle"
                />
                <v-icon
                  class="right-icon"
                  v-show="errorMessagesTrusteeFirstName && errorMessagesTrusteeFirstName.length > 0"
                  color="error"
                  :icon="mdiAlertCircle"
                />
              </template>
            </OnexText>
          </v-col>

          <v-col
            cols="12"
            sm="7"
            class="pt-0 pb-0 mt-n2"
            v-if="showBeneficiaryUnderAgeOf18AndTrustee && beneficiaryUnder18 === 'Y'"
          >
            <OnexText
              v-model="trusteeLastName"
              id="trusteeLastName"
              :error-messages="errorMessagesTrusteeLastName ? errorMessagesTrusteeLastName : null"
              @blur="onBlurTrusteeLastName"
              @change="onBlurTrusteeLastName"
              @keyup="onBlurTrusteeLastName"
              :label="$t('L_trustee_last_name')"
              :max-length="50"
              required
            >
              <template #append-inner>
                <v-icon
                  class="right-icon"
                  v-show="trusteeLastNameDirty && errorMessagesTrusteeLastName === ''"
                  color="success"
                  :icon="mdiCheckCircle"
                />
                <v-icon
                  class="right-icon"
                  v-show="errorMessagesTrusteeLastName && errorMessagesTrusteeLastName.length > 0"
                  color="error"
                  :icon="mdiAlertCircle"
                />
              </template>
            </OnexText>
          </v-col>
          <v-col cols="12" sm="7" class="pt-0 pb-0 mt-n2" v-if="showDOBAndPhoneNumber">
            <OnexDatePicker
              data-comp-id="new_userid_dob"
              type="date"
              class="mt-0"
              :label="$t('L_date_of_birth')"
              :date-format="dobFormatLanguage"
              sensitive
              v-model="dateOfBirth"
              :key="$i18n.locale"
              :max="getMaxDateForMinAge"
              :min="getMinDateForMaxAge"
            />
          </v-col>
          <v-col cols="12" sm="7" class="pt-0 pb-0 mt-n2" v-if="showDOBAndPhoneNumber">
            <OnexPhone
              id="new_userid_telephone"
              id-prop="new_userid_telephone"
              data-comp-id="new_userid_telephone"
              v-model="telephone"
              :label="$t('L_telephone')"
            />
          </v-col>
          <v-col cols="12" sm="7" class="pt-0 pb-0 mt-n2" v-if="showEmailAddress">
            <OnexEmail
              data-comp-id="new-userid-email"
              v-model="emailAddress"
              :label="$t('L_email_address')"
            />
          </v-col>
          <v-col cols="12" sm="7" class="pt-0 pb-0 mt-n2" v-if="showAddress">
            <OnexAddressLine1
              data-comp-id="update_contact_details_address_one"
              v-model="address1Data"
              :label="$t('L_Address_Line_1')"
              :auto-capitalize="true"
              :required="isRequiredShowAddress"
              @keyup="onKeyUpAddressLine1"
            />
          </v-col>
          <v-col cols="12" sm="7" class="pt-0 pb-0 mt-n2" v-if="showAddress">
            <OnexAddressLine2
              data-comp-id="update_contact_details_address_two"
              v-model="address2Data"
              :label="$t('L_Address_Line_2')"
              @keyup="onKeyUpAddressLine2"
            />
          </v-col>
          <v-col cols="12" sm="7" class="pt-0 pb-0 mt-n2" v-if="showAddress">
            <OnexCity
              data-comp-id="update_contact_details_city"
              v-model="cityData"
              :label="$t('L_City')"
              :required="isRequiredShowAddress"
              @keyup="onKeyUpCityData"
            />
          </v-col>
          <v-col cols="12" sm="7" class="pt-0 pb-0 mt-n2" v-if="showAddress">
            <OnexState
              data-comp-id="update-contact-details-state"
              v-model="stateData"
              :country="isCanadaPolicy ? Countries.CA : Countries.US"
              id="update_contact_details_state"
              :label="isCanadaPolicy ? $t('L_policy_province') : $t('L_State')"
              :required="isRequiredShowAddress"
              :include-territories="getTerritories"
              :menu-props="{ location: 'bottom center', scrollStrategy: 'close' }"
            />
          </v-col>
          <v-col cols="12" sm="7" class="pt-0 pb-0 mt-n2" v-if="showAddress">
            <OnexZip
              data-comp-id="update-contact-details-zip-code"
              v-model="zipData"
              :label="currentPolicy.isCanadaPolicy ? $t('L_policy_postal_code') : $t('L_Zip_Code')"
              :max-length="10"
              :country="currentPolicy.isCanadaPolicy ? Countries.CA : Countries.US"
              :required="isRequiredShowAddress"
            />
          </v-col>
        </v-row>
      </v-form>

      <!-- If a minor (below the age of 18) is listed as a beneficiary... -->
      <v-row class="gray-background pa-4 mt-4">
        <v-col v-if="isCanadaPolicy" cols="12" class="pl-4 pr-4 pt-4 pb-4">
          <h4 class="text-h4 font-weight-regular">{{ $t('L_canada_important_disclosure') }}</h4>
        </v-col>
        <v-col v-else cols="12" class="pl-4 pr-4 pt-4 pb-4">
          <h4 class="text-h4 font-weight-regular">{{ $t('L_us_and_ny_important_disclosure') }}</h4>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Action Buttons -->
    <v-card-actions>
      <v-row class="d-flex align-center justify-center pt-8 pb-4 mx-0">
        <v-col cols="12" sm="6" md="4" class="pr-4">
          <OnexButton block variant="outlined" @click="showMessageDialog = true">
            {{ $t('L_cancel') }}
          </OnexButton>
        </v-col>
        <v-col cols="12" sm="6" md="4" class="pr-4">
          <OnexButton
            block
            @click="saveProcess"
            :disabled="disablebutton || v$.$errors.length > 0 || v$.$invalid"
          >
            {{ $t('L_Save') }}
          </OnexButton>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions, type PropType } from 'vue';
import MessageDialog from '@/components/MessageDialog.vue';
import DynatraceClient from '@/plugins/dynatracePlugin';
import { mapActions, mapState, mapStores } from 'pinia';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { useGlobalStore } from '@/stores/global/global.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useVuelidate } from '@vuelidate/core';
import { format, subYears } from 'date-fns';
import type { IGetBeneficiariesResponse } from '@/shared/api/models.bff';
import { mdiAlertCircle, mdiCheckCircle, mdiInformation } from '@mdi/js';
import {
  OnexButton,
  OnexSelect,
  OnexRadio,
  OnexRadioGroup,
  OnexDatePicker,
  OnexFirstName,
  OnexMiddleName,
  OnexLastName,
  OnexSsn,
  OnexEmail,
  OnexAddressLine1,
  OnexAddressLine2,
  OnexCity,
  OnexState,
  OnexPhone,
  Countries,
  OnexZip,
  OnexAllocation,
  OnexText,
  UILocale,
  DateFormats,
  SSNFormat,
  UsTerritories,
  type USTerritories,
} from '@primerica/onex-ui-lib';
import { RouteNames } from '@/router/routeNames';
import { $t } from '@/i18n/i18n';

export const firstNameValidations = (value: string) =>
  value !== '' && value.trim().length >= 2 && /^([A-Za-z][A-Za-z \-.']+)$/.test(value)
    ? true
    : $t('L_firstname_invalid_msg');

export const lastNameValidations = (value: string) =>
  value !== '' && value.trim().length >= 2 && /^([A-Za-z][A-Za-z \-.']+)$/.test(value)
    ? true
    : $t('L_lastname_invalid_msg');

export const middleNameValidations = (value: string) =>
  value === ''
    ? true
    : /^([A-Za-z][A-Za-z \-.']+)$/.test(value)
      ? true
      : $t('L_middle_name_is_invalid');

const componentOptions: ComponentOptions = {
  name: 'EditBeneficiaryDetails',
  components: {
    MessageDialog,
    OnexButton,
    OnexSelect,
    OnexRadio,
    OnexRadioGroup,
    OnexDatePicker,
    OnexFirstName,
    OnexMiddleName,
    OnexLastName,
    OnexSsn,
    OnexEmail,
    OnexAddressLine1,
    OnexAddressLine2,
    OnexCity,
    OnexState,
    OnexPhone,
    OnexZip,
    OnexAllocation,
    OnexText,
  },
  props: {
    beneObj: {
      type: Object as PropType<IGetBeneficiariesResponse>,
      required: false,
      default: () => {},
    },
    beneType: {
      type: String,
      required: false,
      default: '',
    },
  },
  data: () => ({
    hasFinished: false,
    fullNameDirty: false,
    trusteeFirstNameDirty: false,
    trusteeLastNameDirty: false,
    beneficiaryType: null as unknown as null | string,
    allocationPercentage: '',
    firstName: '',
    middleName: '',
    lastName: '',
    fullName: '',
    ssn: '',
    relationshipToInsured: '',
    beneficiaryUnder18: '',
    trusteeFirstName: '',
    trusteeLastName: '',
    emailAddress: '',
    address1Data: '',
    address2Data: '',
    cityData: '',
    stateData: '',
    zipData: '',
    telephone: '',
    dateOfBirth: undefined,
    dateSigned: '',
    updateUserId: '',
    irrevocable: false,
    revocable: true,
    errorMessagesFullName: '',
    errorMessagesTrusteeFirstName: '',
    errorMessagesTrusteeLastName: '',
    // usStateData: null as any,
    // caTerritoryData: null as any,
    cloneBeneficiary: null as unknown as IGetBeneficiariesResponse,
    isDupFirstName: false,
    isDupMiddleName: false,
    isDupLastName: false,
    showRevocableCheckbox: false,
    showMessageDialog: false,
    v$: useVuelidate(),
    mdiCheckCircle,
    mdiAlertCircle,
    mdiInformation,
    Countries,
    validForm: false,
    DateFormats,
  }),
  validations() {
    return {
      firstName: {
        firstNameValidations: firstNameValidations,
      },
      middleName: {
        middleNameValidations: middleNameValidations,
      },
      lastName: {
        lastNameValidations: lastNameValidations,
      },
    };
  },
  computed: {
    SSNFormat() {
      return SSNFormat;
    },
    ...mapStores(usePolicyStore),
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(useGlobalStore, { locale: 'getLocale' }),
    ...mapState(usePolicyStore, {
      beneficiaryStep: 'beneficiaryStep',
      beneficiaries: 'beneficiaries',
      currentPolicy: 'currentPolicy',
      beneficiaryPrimary: 'beneficiaryPrimary',
      beneficiaryPrimaryContingent: 'beneficiaryPrimaryContingent',
      beneficiarySpouse: 'beneficiarySpouse',
      beneficiarySpouseContingent: 'beneficiarySpouseContingent',
    }),
    getTerritories(): USTerritories[] {
      return [
        UsTerritories.AMERICAN_SAMOA,
        UsTerritories.GUAM,
        UsTerritories.NORTHERN_MARIANA_ISLANDS,
        UsTerritories.PUERTO_RICO,
        UsTerritories.VIRGIN_ISLANDS,
      ];
    },
    getMaxDateForMinAge() {
      const max = new Date(); // max date is current date; wait for AC to confirm
      return max;
    },
    getMinDateForMaxAge() {
      const min = subYears(new Date(), 100);
      return min;
    },
    getSSNLabel() {
      return this.$i18n.locale !== 'en-US' ? this.$t('L_Social_Security_Number') : this.$t('L_ssn');
    },
    dobFormatLanguage() {
      return this.$i18n.locale === UILocale.FR_CA.toString()
        ? DateFormats.DDMMYYYY
        : DateFormats.MMDDYYYY;
    },
    beneficiaryUnder18YesAndNo() {
      return [
        {
          name: this.$t('L_YES'),
          label: this.$t('L_YES'),
          value: 'Y',
        },
        {
          name: this.$t('L_NO'),
          label: this.$t('L_NO'),
          value: 'N',
        },
      ];
    },
    enterInfoText() {
      return (
        this.$t('L_Enter_information_for_NAMEs_primaryORcontingent_beneficiary', {
          name: this.getNameOnPolicy,
          primorcont: this.getPrimaryOrContingent,
        }) || ''
      );
    },
    isUsPolicy() {
      return this.currentPolicy.policyData?.isUSPolicy;
    },
    isNyPolicy() {
      return this.currentPolicy?.isNewYorkPolicy;
    },
    isCanadaPolicy() {
      return !!this.currentPolicy?.isCanadaPolicy;
    },
    isQuebecPolicy() {
      return this.currentPolicy?.isCanadaQuebecBeneficiaryPolicy;
    },
    selectHint() {
      return 'TODO hint';
    },
    primaryInsured() {
      return `${this.currentPolicy?.primaryInsured?.firstName} ${this.currentPolicy?.primaryInsured?.lastName}`;
    },
    spouseInsured() {
      if (this.currentPolicy?.data?.spouseLastName) {
        return `${this.currentPolicy?.data?.spouseFirstName} ${this.currentPolicy?.data?.spouseLastName}`;
      }
      return this.$t('L_policy_beneficiaries_spouse');
    },
    showDOBAndPhoneNumber() {
      let res = false;
      if (this.beneficiaryType === 'Y' && !this.isCanadaPolicy) {
        res = true;
      }
      return res;
    },
    showName() {
      let res = false;
      if (this.beneficiaryType === 'Y') {
        res = true;
      }
      return res;
    },
    showFullName() {
      let res = false;
      if (this.beneficiaryType === 'X') {
        res = true;
      }
      return res;
    },
    showSSNorSIN() {
      let res = false;
      if (this.beneficiaryType === 'Y' && !this.isCanadaPolicy) {
        res = true;
      }
      return res;
    },
    showRelationshipToInsured() {
      let res = false;
      if (this.beneficiaryType === 'Y') {
        res = true;
      }
      return res;
    },
    showEmailAddress() {
      let res = false;
      if (this.beneficiaryType === 'Y' || this.beneficiaryType === 'X') {
        res = true;
      }
      return res;
    },
    showBeneficiaryUnderAgeOf18AndTrustee() {
      let res = false;
      if (this.beneficiaryType === 'Y' || this.beneficiaryType === 'C') {
        if (this.isCanadaPolicy) {
          res = true;
        }
      }
      return res;
    },
    showAddress() {
      let res = false;
      if (this.beneficiaryType === 'Y' || this.beneficiaryType === 'X') {
        res = true;
      }
      return res;
    },
    isRequiredShowAddress() {
      let res = false;
      if (this.beneficiaryType === 'Y' && this.isNyPolicy) {
        res = true;
      }
      return res;
    },
    getNameOnPolicy() {
      if (this.beneObj?.beneKeyId && this.beneficiaryStep === 3) {
        // For edit we pass the entire beneficiary object as beneObj which contains the id as beneKeyId from which we can pull the object we need and
        // check the beneficiaryType and pull the Primary | Spouse accordingly
        if (this.beneObj.beneficiaryType === 'P' || this.beneObj.beneficiaryType === 'C') {
          return this.primaryInsured || '';
        }
        // const obj = this.beneficiaries.filter(bene => bene.beneKeyId === this.beneId);
        // if (obj && (obj[0].beneficiaryType === 'P' || obj[0].beneficiaryType === 'C')) {
        //   return this.primaryInsured;
        // }
        return this.spouseInsured || '';
      }
      // if it's add we pass the beneType so we can use that to pull primary | spouse
      if (this.beneficiaryStep === 2 && (this.beneType === 'P' || this.beneType === 'C')) {
        return this.primaryInsured;
      }
      return this.spouseInsured;
    },
    getPrimaryOrContingent() {
      if (this.beneObj?.beneKeyId !== '' && this.beneficiaryStep === 3) {
        // For edit we pass the entire beneficiary obj in beneObj
        // check the beneficiaryType and pull the Primary if 'P' or 'S'
        if (this.beneObj.beneficiaryType === 'P' || this.beneObj.beneficiaryType === 'S') {
          return this.$t('L_primary') || '';
        }
        // const obj = this.beneficiaries.filter(bene => bene.beneKeyId === this.beneId);
        // if (obj && (obj[0].beneficiaryType === 'P' || obj[0].beneficiaryType === 'S')) {
        //   return this.$t('L_primary');
        // }
        return this.$t('L_contingent') || '';
      }
      // if it's add we pass the beneType so we can use that to pull Primary if 'P' or 'S'
      if (this.beneficiaryStep === 2 && (this.beneType === 'P' || this.beneType === 'S')) {
        return this.$t('L_primary');
      }
      return this.$t('L_contingent');
    },
    beneficiaryTypeList() {
      const benTypeOptions = [];

      // Individual
      benTypeOptions.push({
        value: 'Y',
        title: this.$t('L_individual'),
      });

      // All Children Equally
      const benType = this.beneficiaryStep === 2 ? this.beneType : this.beneObj.beneficiaryType;
      let fullName = 'All Children Equally';
      if (benType === 'P' || benType === 'C') {
        const _alreadyExists =
          this.beneficiaryPrimary.filter(
            (benObj: IGetBeneficiariesResponse) =>
              benObj.fullName === fullName && benObj.beneKeyId !== this.cloneBeneficiary?.beneKeyId
          ) +
            this.beneficiaryPrimaryContingent.filter(
              (benObj: IGetBeneficiariesResponse) =>
                benObj.fullName === fullName &&
                benObj.beneKeyId !== this.cloneBeneficiary?.beneKeyId
            ) !==
          '';
        if (!_alreadyExists) {
          benTypeOptions.push({
            value: 'C',
            title: this.$t('L_all_children_equally'),
          });
        }
      } else if (benType === 'S' || benType === 'H') {
        const _alreadyExists =
          this.beneficiarySpouse.filter(
            (benObj: IGetBeneficiariesResponse) =>
              benObj.fullName === fullName && benObj.beneKeyId !== this.cloneBeneficiary?.beneKeyId
          ) +
            this.beneficiarySpouseContingent.filter(
              (benObj: IGetBeneficiariesResponse) =>
                benObj.fullName === fullName &&
                benObj.beneKeyId !== this.cloneBeneficiary?.beneKeyId
            ) !==
          '';
        if (!_alreadyExists) {
          benTypeOptions.push({
            value: 'C',
            title: this.$t('L_all_children_equally'),
          });
        }
      } else {
        benTypeOptions.push({
          value: 'C',
          title: this.$t('L_all_children_equally'),
        });
      }

      // Group, Organizations or Entity
      benTypeOptions.push({
        value: 'X',
        title: this.$t('L_group_organizations_or_entity'),
      });

      // Estate
      fullName = 'Estate';
      if (benType === 'P' || benType === 'C') {
        const _alreadyExists =
          this.beneficiaryPrimary.filter(
            (benObj: IGetBeneficiariesResponse) =>
              benObj.fullName === fullName && benObj.beneKeyId !== this.cloneBeneficiary?.beneKeyId
          ) +
            this.beneficiaryPrimaryContingent.filter(
              (benObj: IGetBeneficiariesResponse) =>
                benObj.fullName === fullName &&
                benObj.beneKeyId !== this.cloneBeneficiary?.beneKeyId
            ) !==
          '';
        if (!_alreadyExists) {
          benTypeOptions.push({
            value: 'E',
            title: this.$t('L_estate'),
          });
        }
      } else if (benType === 'S' || benType === 'H') {
        const _alreadyExists =
          this.beneficiarySpouse.filter(
            (benObj: IGetBeneficiariesResponse) =>
              benObj.fullName === fullName && benObj.beneKeyId !== this.cloneBeneficiary?.beneKeyId
          ) +
            this.beneficiarySpouseContingent.filter(
              (benObj: IGetBeneficiariesResponse) =>
                benObj.fullName === fullName &&
                benObj.beneKeyId !== this.cloneBeneficiary?.beneKeyId
            ) !==
          '';
        if (!_alreadyExists) {
          benTypeOptions.push({
            value: 'E',
            title: this.$t('L_estate'),
          });
        }
      } else {
        benTypeOptions.push({
          value: 'E',
          title: this.$t('L_estate'),
        });
      }

      return benTypeOptions;
    },

    relationshipToInsuredList() {
      interface iRetObj {
        value: string;
        title: string;
      }

      let retObj: iRetObj[] = [];
      if (this.beneficiaryType === 'C') {
        retObj.push({
          value: '',
          title: '',
        });
      } else if (this.beneficiaryType === 'E') {
        retObj.push({
          value: '',
          title: '',
        });
      } else if (this.beneficiaryType === 'X') {
        retObj.push({
          value: '',
          title: '',
        });
      } else if (this.beneficiaryType === 'Y') {
        let isSpouseAlreadyUsedInPrimaryOrContingent = false;
        if (
          this.beneType === 'P' ||
          this.beneType === 'C' ||
          this.cloneBeneficiary?.beneficiaryType === 'P' ||
          this.cloneBeneficiary?.beneficiaryType === 'C'
        ) {
          if (
            this.beneficiaryPrimary.filter(
              (obj: IGetBeneficiariesResponse) =>
                String(obj.beneficiaryEntityType) === 'Y' && String(obj.relationship) === 'Spouse'
            ).length +
              this.beneficiaryPrimaryContingent.filter(
                (obj: IGetBeneficiariesResponse) =>
                  String(obj.beneficiaryEntityType) === 'Y' && String(obj.relationship) === 'Spouse'
              ).length >
            0
          ) {
            isSpouseAlreadyUsedInPrimaryOrContingent = true;
          }
        }
        if (
          this.beneType === 'S' ||
          this.beneType === 'H' ||
          this.cloneBeneficiary?.beneficiaryType === 'S' ||
          this.cloneBeneficiary?.beneficiaryType === 'H'
        ) {
          if (
            this.beneficiarySpouse.filter(
              (obj: IGetBeneficiariesResponse) =>
                String(obj.beneficiaryEntityType) === 'Y' && String(obj.relationship) === 'Spouse'
            ).length +
              this.beneficiarySpouseContingent.filter(
                (obj: IGetBeneficiariesResponse) =>
                  String(obj.beneficiaryEntityType) === 'Y' && String(obj.relationship) === 'Spouse'
              ).length >
            0
          ) {
            isSpouseAlreadyUsedInPrimaryOrContingent = true;
          }
        }
        let isCurrentUserRelationshipSpouse = false;
        if (this.cloneBeneficiary?.relationship === 'Spouse') {
          isCurrentUserRelationshipSpouse = true;
        }
        if (isSpouseAlreadyUsedInPrimaryOrContingent && !isCurrentUserRelationshipSpouse) {
          retObj = [
            {
              value: 'Child',
              title: this.$t('L_child'),
            },
            {
              value: 'Sibling',
              title: this.$t('L_sibling'),
            },
            {
              value: 'Parent',
              title: this.$t('L_parent'),
            },
            {
              value: 'Relative',
              title: this.$t('L_relative'),
            },
            {
              value: 'Friend',
              title: this.$t('L_friend'),
            },
            {
              value: 'Other',
              title: this.$t('L_other'),
            },
          ];
          if (this.isCanadaPolicy) {
            retObj.push({
              value: 'Common Law Spouse',
              title: this.$t('L_common_law_spouse'),
            });
          }
        } else {
          retObj = [
            {
              value: 'Spouse',
              title: this.$t('L_spouse'),
            },
            {
              value: 'Child',
              title: this.$t('L_child'),
            },
            {
              value: 'Sibling',
              title: this.$t('L_sibling'),
            },
            {
              value: 'Parent',
              title: this.$t('L_parent'),
            },
            {
              value: 'Relative',
              title: this.$t('L_relative'),
            },
            {
              value: 'Friend',
              title: this.$t('L_friend'),
            },
            {
              value: 'Other',
              title: this.$t('L_other'),
            },
          ];
          if (this.isCanadaPolicy) {
            retObj.push({
              value: 'Common Law Spouse',
              title: this.$t('L_common_law_spouse'),
            });
          }
        }
      }
      return retObj;
    },
    disablebutton() {
      let disableButton = false;
      if (this.beneficiaryType === 'C') {
        if (this.isCanadaPolicy) {
          if (this.beneficiaryUnder18 === 'Y') {
            if (this.trusteeFirstName.length === 0 || this.errorMessagesTrusteeFirstName !== '') {
              disableButton = true;
            }
            if (this.trusteeLastName.length === 0 || this.errorMessagesTrusteeLastName !== '') {
              disableButton = true;
            }
          } else if (this.beneficiaryUnder18 === '') {
            disableButton = true;
          }
        } else {
          disableButton = false;
        }
      } else if (this.beneficiaryType === 'E') {
        disableButton = false;
      } else if (this.beneficiaryType === 'X') {
        if (
          parseFloat(this.allocationPercentage) <= 0 ||
          parseFloat(this.allocationPercentage) > 100
        ) {
          disableButton = true;
        }
        if (this.fullName.length === 0 || this.errorMessagesFullName.length > 0) {
          disableButton = true;
        }
      } else if (this.beneficiaryType === 'Y') {
        if (
          parseFloat(this.allocationPercentage) <= 0 ||
          parseFloat(this.allocationPercentage) > 100
        ) {
          disableButton = true;
        }
        if (this.isCanadaPolicy) {
          if (this.beneficiaryUnder18 === 'Y') {
            if (this.trusteeFirstName.length === 0 || this.errorMessagesTrusteeFirstName !== '') {
              disableButton = true;
            }
            if (this.trusteeLastName.length === 0 || this.errorMessagesTrusteeLastName !== '') {
              disableButton = true;
            }
          } else if (this.beneficiaryUnder18 === '') {
            disableButton = true;
          }
        }
      } else {
        disableButton = true;
      }

      return disableButton;
    },
  },
  methods: {
    ...mapActions(usePolicyStore, {
      setBeneficiaryStep: 'setBeneficiaryStep',
      setBeneficiaryPrimary: 'setBeneficiaryPrimary',
      setBeneficiaryPrimaryContingent: 'setBeneficiaryPrimaryContingent',
      setBeneficiarySpouse: 'setBeneficiarySpouse',
      setBeneficiarySpouseContingent: 'setBeneficiarySpouseContingent',
    }),
    onKeyUpAddressLine1() {
      this.address1Data = this.address1Data.replace(
        /^(\d*\s*)([a-zA-Z])/,
        (_: string, prefix: string, letter: string) => {
          return prefix + letter.toUpperCase();
        }
      );
    },
    onKeyUpAddressLine2() {
      this.address2Data = this.address2Data.replace(
        /^(\d*\s*)([a-zA-Z])/,
        (_: string, prefix: string, letter: string) => {
          return prefix + letter.toUpperCase();
        }
      );
    },
    onKeyUpCityData() {
      this.cityData = this.cityData.charAt(0).toUpperCase() + this.cityData.slice(1);
    },
    isSSNRequired() {
      return this.isUsPolicy;
    },
    isBenObjSameAsCurrBenTypeDropdown(val: string) {
      let isBenObjSameAsCurrBenType = false;
      if (this.beneficiaryType.toUpperCase() === 'C' && val.toUpperCase() === 'C') {
        isBenObjSameAsCurrBenType = true;
      } else if (this.beneficiaryType.toUpperCase() === 'E' && val.toUpperCase() === 'E') {
        isBenObjSameAsCurrBenType = true;
      } else if (
        this.beneficiaryType.toUpperCase() === 'X' &&
        (val.toUpperCase() === 'X' || val.toUpperCase() === 'O')
      ) {
        isBenObjSameAsCurrBenType = true;
      } else if (
        this.beneficiaryType.toUpperCase() === 'Y' &&
        (val.toUpperCase() === 'I' ||
          val.toUpperCase() === 'P' ||
          val.toUpperCase() === 'S' ||
          val.toUpperCase() === 'Y')
      ) {
        isBenObjSameAsCurrBenType = true;
      }
      return isBenObjSameAsCurrBenType;
    },
    onBlurFullName() {
      this.fullNameDirty = true;
      if (this.fullNameAlreadyExists()) {
        this.errorMessagesFullName = this.$t('L_name_has_already_been_used');
      } else if (this.fullName === '') {
        this.errorMessagesFullName = this.$t('L_Full_name_is_required');
      } else if (this.fullName.trim().length < 3) {
        this.errorMessagesFullName = this.$t('L_full_name_must_be_at_least');
      } else if (this.fullName.match(/^([A-Za-z][A-Za-z \-.']+)$/)) {
        this.errorMessagesFullName = '';
      } else {
        this.errorMessagesFullName = this.$t('L_Full_name_is_invalid');
      }
    },
    revFullNameAlreadyExists() {
      return !this.fullNameAlreadyExists();
    },
    cityMaxReached() {
      return this.cityData.length <= 25;
    },
    relationshipComparisonSame(val: string) {
      let isBenObjSameAsCurrBenType = false;
      if (this.relationshipToInsured.toUpperCase() === val.toUpperCase()) {
        isBenObjSameAsCurrBenType = true;
      }
      return isBenObjSameAsCurrBenType;
    },
    fullNameAlreadyExists() {
      const alreadyExists = false;
      return alreadyExists;
    },
    onBlurTrusteeFirstName() {
      this.trusteeFirstNameDirty = true;
      if (this.trusteeFirstName.match(/^([A-Za-z][A-Za-z \-.']+)$/)) {
        this.errorMessagesTrusteeFirstName = '';
      } else if (this.trusteeFirstName === '') {
        this.errorMessagesTrusteeFirstName = this.$t('L_Trustee_First_Name_is_required');
      } else {
        this.errorMessagesTrusteeFirstName = this.$t('L_Trustee_First_Name_is_invalid');
      }
    },
    onBlurTrusteeLastName() {
      this.trusteeLastNameDirty = true;
      if (this.trusteeLastName.match(/^([A-Za-z][A-Za-z \-.']+)$/)) {
        this.errorMessagesTrusteeLastName = '';
      } else if (this.trusteeLastName === '') {
        this.errorMessagesTrusteeLastName = this.$t('L_Trustee_Last_Name_is_required');
      } else {
        this.errorMessagesTrusteeLastName = this.$t('L_Trustee_Last_Name_is_invalid');
      }
    },
    closeThisPage() {
      try {
        this.$emit('cancel');
        const beneficiaryType =
          this.beneficiaryStep === 3 ? this.cloneBeneficiary.beneficiaryType : this.beneType;
        const tempDynatraceString = `PC2_BenfDetails_Cancel_Policynum-${this.currentPolicy?.contractId}_BeneficiaryType-${beneficiaryType}_PesonType-${this.beneficiaryType}`;
        // const tempStr = 'PC2_UpdateBenf_Edit_Policynum-' + <policy>_BeneficiaryType-[P,C,S,H]_Name-<Full Name - first 30 characters, replace spaces with underlines>
        const i = DynatraceClient.enterAction(tempDynatraceString);
        DynatraceClient.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
      this.policyStore.beneficiaryStep = 1;
      this.$router.push({
        name: RouteNames.BENEFICIARY_STEP,
        params: {},
      });
    },
    saveProcess() {
      let temp;
      if (this.beneficiaryUnder18 !== 'Y') {
        this.trusteeFirstName = '';
        this.trusteeLastName = '';
      }
      if (this.dateOfBirth === '') {
        this.dateOfBirth = undefined;
      }

      const currData = {
        beneficiaryPercent: Number(this.allocationPercentage),
        address: {
          addressLine1: this.address1Data,
          state: this.stateData,
          addressLine2: this.address2Data,
          city: this.cityData,
          country: this.isCanadaPolicy ? 'CA' : 'US',
          zipCode: this.zipData,
        },
        origin: 'A', // Are we using A
        relationship: this.relationshipToInsured,
        emailAddress: this.emailAddress,
        dateOfBirth:
          this.dateOfBirth === undefined ? undefined : format(this.dateOfBirth, 'yyyy-MM-dd'),
        telephone: this.telephone,
        trusteeFirstName: this.trusteeFirstName,
        applicationSource: 'PC2',
        middleName: this.middleName,
        firstName: this.firstName,
        // Setting to a negative number if not existing else reuse same number.
        beneKeyId:
          this.cloneBeneficiary?.beneKeyId > 0
            ? this.cloneBeneficiary.beneKeyId
            : -1 *
              (this.beneficiaryPrimary.length +
                this.beneficiaryPrimaryContingent.length +
                this.beneficiarySpouse.length +
                this.beneficiarySpouseContingent.length),
        // TODO need to add P, C, S, H  this is not the this.beneficiaryType which has C, E, X, Y
        beneficiaryType:
          this.beneficiaryStep === 2 ? this.beneType : this.cloneBeneficiary.beneficiaryType,
        trusteeLastName: this.trusteeLastName,
        irrevocable: this.irrevocable,
        ssn: this.ssn,
        lastName: this.lastName,
        fullName: '',
        // TODO for now we think this is the right place to put C, E, X, Y
        beneficiaryEntityType: this.beneficiaryType, // What is this? is this the PERSON_TYPE in LIFE_BENEFICIARY
        dateSigned: null,
        updateUserId: null,
      };
      // FYI we have this element named after the label however this is not the 'Primary', 'Contingent' for Main or Spouse
      // It is instead the selection of the kind of beneficary template they want to use such as Estate, Entity, Individual or All Children Equally
      if (this.beneficiaryType === 'C') {
        // All Children
        currData.fullName = 'All Children Equally';
      } else if (this.beneficiaryType === 'E') {
        // Estate
        currData.fullName = 'Estate';
      } else if (this.beneficiaryType === 'X') {
        // Entity
        currData.fullName = this.fullName;
      } else if (this.beneficiaryType === 'Y') {
        // Individual
        const fName = this.firstName.trim() === '' ? '' : `${this.firstName.trim()} `;
        const mName = this.middleName.trim() === '' ? '' : `${this.middleName.trim()} `;
        const lName = this.lastName.trim() === '' ? '' : this.lastName.trim();
        currData.fullName = (fName + mName + lName).trim().substring(0, 75);
      }
      // if beneficiaryStep 2 Add mode this.beneType
      if (this.beneficiaryStep === 2) {
        if (this.beneType === 'P') {
          temp = this.beneficiaryPrimary;
          temp.push(currData as unknown as IGetBeneficiariesResponse);
          this.beneficiaryPrimary = temp;
        } else if (this.beneType === 'C') {
          temp = this.beneficiaryPrimaryContingent;
          temp.push(currData as unknown as IGetBeneficiariesResponse);
          this.beneficiaryPrimaryContingent = temp;
        } else if (this.beneType === 'S') {
          temp = this.beneficiarySpouse;
          temp.push(currData as unknown as IGetBeneficiariesResponse);
          this.beneficiarySpouse = temp;
        } else if (this.beneType === 'H') {
          temp = this.beneficiarySpouseContingent;
          temp.push(currData as unknown as IGetBeneficiariesResponse);
          this.beneficiarySpouseContingent = temp;
        }
      } else if (this.beneficiaryStep === 3) {
        // if beneficiaryStep 3 Edit mode this.beneObj
        // irrevocable we use existing data for edit, for new one it will be false
        currData.dateSigned = this.cloneBeneficiary.dateSigned;
        currData.updateUserId = this.cloneBeneficiary.updateUserId;
        if (this.cloneBeneficiary.beneficiaryType === 'P') {
          temp = this.beneficiaryPrimary.filter(
            (ben: IGetBeneficiariesResponse) => ben.beneKeyId !== this.cloneBeneficiary.beneKeyId
          );
          temp.push(currData as unknown as IGetBeneficiariesResponse);
          this.setBeneficiaryPrimary(temp);
        } else if (this.cloneBeneficiary.beneficiaryType === 'C') {
          temp = this.beneficiaryPrimaryContingent.filter(
            (ben: IGetBeneficiariesResponse) => ben.beneKeyId !== this.cloneBeneficiary.beneKeyId
          );
          temp.push(currData as unknown as IGetBeneficiariesResponse);
          this.setBeneficiaryPrimaryContingent(temp);
        } else if (this.cloneBeneficiary.beneficiaryType === 'S') {
          temp = this.beneficiarySpouse.filter(
            (ben: IGetBeneficiariesResponse) => ben.beneKeyId !== this.cloneBeneficiary.beneKeyId
          );
          temp.push(currData as unknown as IGetBeneficiariesResponse);
          this.setBeneficiarySpouse(temp);
        } else if (this.cloneBeneficiary.beneficiaryType === 'H') {
          temp = this.beneficiarySpouseContingent.filter(
            (ben: IGetBeneficiariesResponse) => ben.beneKeyId !== this.cloneBeneficiary.beneKeyId
          );
          temp.push(currData as unknown as IGetBeneficiariesResponse);
          this.setBeneficiarySpouseContingent(temp);
        }
      }
      this.hasFinished = true;
      try {
        const beneficiaryType =
          this.beneficiaryStep === 3 ? this.cloneBeneficiary.beneficiaryType : this.beneType;
        const tempDynatraceString = `PC2_BenfDetails_Save_Policynum-${this.currentPolicy?.contractId}_BeneficiaryType-${beneficiaryType}_PesonType-${this.beneficiaryType}`;
        // const tempStr = 'PC2_UpdateBenf_Edit_Policynum-' + <policy>_BeneficiaryType-[P,C,S,H]_Name-<Full Name - first 30 characters, replace spaces with underlines>
        const i = DynatraceClient.enterAction(tempDynatraceString);
        DynatraceClient.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
      this.resetForm();
      // await saveCall
      this.policyStore.isBeneficiaryDataDirty = true;
      this.policyStore.beneficiaryStep = 1;
      this.$router.push({
        name: RouteNames.BENEFICIARY_STEP,
        params: {},
      });
    },
    resetForm() {
      // Not resetting data if it's edit this.beneficiaryStep !== 3 &&
      if (!this.hasFinished) {
        this.fullNameDirty = false;
        this.trusteeFirstNameDirty = false;
        this.trusteeLastNameDirty = false;
        this.allocationPercentage = '';
        this.firstName = '';
        this.middleName = '';
        this.lastName = '';
        this.fullName = '';
        this.ssn = '';
        this.relationshipToInsured = '';
        this.beneficiaryUnder18 = '';
        this.trusteeFirstName = '';
        this.trusteeLastName = '';
        this.emailAddress = '';
        this.dateOfBirth = undefined;
        this.telephone = '';
        this.address1Data = '';
        this.address2Data = '';
        this.cityData = '';
        this.stateData = '';
        this.zipData = '';
        this.errorMessagesFullName = '';
        this.errorMessagesTrusteeFirstName = '';
        this.errorMessagesTrusteeLastName = '';
      }
    },
  },
  watch: {
    locale(newData, oldData) {
      if (newData !== oldData) {
        this.onBlurFullName();
        this.onBlurTrusteeFirstName();
        this.onBlurTrusteeLastName();
      }
    },
    stateData(newVal, oldVal) {
      if (newVal !== oldVal && newVal) {
        if (!this.isCanadaPolicy) {
          this.v$.usStateData.$model = this.stateData;
        } else {
          this.v$.caTerritoryData.$model = this.stateData;
        }
      }
    },
    revocable(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.irrevocable = !newVal;
      }
    },
    firstName(newVal, oldVal) {
      if (newVal !== oldVal && newVal) {
        if (this.fullNameAlreadyExists()) {
          this.isDupFirstName = true;
        }
        this.firstName = newVal.charAt(0).toUpperCase() + newVal.slice(1);
      }
    },
    middleName(newVal, oldVal) {
      if (newVal !== oldVal && newVal) {
        if (this.fullNameAlreadyExists()) {
          this.isDupMiddleName = true;
        }
        this.middleName = newVal.charAt(0).toUpperCase() + newVal.slice(1);
      }
    },
    lastName(newVal, oldVal) {
      if (newVal !== oldVal && newVal) {
        if (this.fullNameAlreadyExists()) {
          this.isDupLastName = true;
        }
        this.lastName = newVal.charAt(0).toUpperCase() + newVal.slice(1);
      }
    },
    relationshipToInsured(newVal, oldVal) {
      if (newVal !== oldVal && newVal) {
        // QuebecPolicy with relationship of Spouse and Individual selected for BeneficiaryType
        if (this.isQuebecPolicy && newVal === 'Spouse' && this.beneficiaryType === 'Y') {
          // set irrevocable to true
          // set flag to show the checkbox having verbiage for Irrevocable Beneficiary under the relationship
          this.showRevocableCheckbox = true;
          // if (this.v$.$anyDirty) {
          this.irrevocable = true;
          this.revocable = false;
          // }
        } else {
          this.showRevocableCheckbox = false;
        }
      }
    },
    // fullName(newVal, oldVal) {
    //   if (newVal !== oldVal && newVal) {
    //     if (this.fullNameAlreadyExists()) {
    //       this.errorMessagesFullName = this.$t('L_name_has_already_been_used');
    //     } else {
    //       this.errorMessagesFullName = '';
    //     }
    //   }
    // },
    beneficiaryType(newVal, oldVal) {
      if (newVal !== oldVal && newVal && oldVal !== null) {
        this.resetForm();
        this.v$.$reset();
        this.relationshipToInsured = '';
        this.showRevocableCheckbox = false;
        if (newVal === 'E') {
          // Estate
          this.allocationPercentage = '100';
          this.fullName = 'Estate';
          this.relationshipToInsured = 'Estate';
        } else if (newVal === 'C') {
          // All Children Equally
          this.allocationPercentage = '100';
          this.fullName = 'All Children Equal';
        } else if (newVal === 'X' && this.beneficiaryStep === 2) {
          // Entity
          this.allocationPercentage = '';
          this.fullName = '';
        } else if (newVal === 'Y' && this.beneficiaryStep === 2) {
          // Individual
          this.allocationPercentage = '';
          this.fullName = '';
          if (this.relationshipToInsured === 'Spouse' && this.isQuebecPolicy) {
            this.irrevocable = true;
            this.revocable = false;
            this.showRevocableCheckbox = true;
          } else {
            this.showRevocableCheckbox = false;
          }
        }
        this.$nextTick(() => {
          if (oldVal !== '' && this.stateData !== '') {
            this.stateData = '';
            this.$nextTick(() => {
              if (!this.isCanadaPolicy) {
                this.v$.usStateData.$model = '';
              } else {
                this.v$.caTerritoryData.$model = '';
              }
            });
            this.$nextTick(() => {
              if (!this.isCanadaPolicy) {
                this.v$.usStateData.reset();
              } else {
                this.v$.caTerritoryData.reset();
              }
            });
          }
        });
      }
    },
  },
  mounted() {
    this.beneficiaryType = '';
    this.resetForm();

    // This is for edit we want to set the beneType i.e. 'P', 'C', 'S', 'H'
    if (this.beneficiaryStep === 2) {
      // TODO what needs to go here for add since this is add
    } else if (this.beneficiaryStep === 3) {
      this.cloneBeneficiary = { ...this.beneObj };
      this.beneficiaryType = this.cloneBeneficiary.beneficiaryEntityType;
      // TODO beneficiaryEntityType
      if (
        this.cloneBeneficiary.beneficiaryEntityType === 'I' ||
        this.cloneBeneficiary.beneficiaryEntityType === 'P' ||
        this.cloneBeneficiary.beneficiaryEntityType === 'S' ||
        this.cloneBeneficiary.beneficiaryEntityType === 'Y'
      ) {
        this.beneficiaryType = 'Y';
      }
      if (
        this.cloneBeneficiary.beneficiaryEntityType === 'X' ||
        this.cloneBeneficiary.beneficiaryEntityType === 'O'
      ) {
        this.beneficiaryType = 'X';
      }
      if (this.cloneBeneficiary.beneficiaryEntityType === 'C') {
        this.beneficiaryType = 'C';
      }
      if (this.cloneBeneficiary.beneficiaryEntityType === 'E') {
        this.beneficiaryType = 'E';
      }
    }
    if (this.beneficiaryStep === 3) {
      this.allocationPercentage = this.cloneBeneficiary.beneficiaryPercent.toString();
      this.address1Data = this.cloneBeneficiary.address.addressLine1;
      this.stateData = this.cloneBeneficiary.address.state;
      this.address2Data = this.cloneBeneficiary.address.addressLine2;
      this.cityData = this.cloneBeneficiary.address.city;
      this.zipData = this.cloneBeneficiary.address.zipCode;
      this.relationshipToInsured = this.cloneBeneficiary.relationship;
      this.trusteeFirstName = this.cloneBeneficiary.trusteeFirstName;
      this.middleName = this.cloneBeneficiary.middleName;
      this.firstName = this.cloneBeneficiary.firstName;
      this.trusteeLastName = this.cloneBeneficiary.trusteeLastName;
      this.irrevocable = this.cloneBeneficiary.irrevocable;
      this.revocable = !this.cloneBeneficiary.irrevocable;
      this.dateOfBirth = this.cloneBeneficiary.dateOfBirth
        ? new Date(this.cloneBeneficiary.dateOfBirth)
        : undefined;
      if (
        this.isQuebecPolicy &&
        this.relationshipToInsured === 'Spouse' &&
        this.beneficiaryType === 'Y'
      ) {
        this.showRevocableCheckbox = true;
      }
      this.ssn = this.cloneBeneficiary.ssn || '';
      this.lastName = this.cloneBeneficiary.lastName;
      this.fullName = this.cloneBeneficiary.fullName;
      this.emailAddress = this.cloneBeneficiary.emailAddress;
      if (this.showDOBAndPhoneNumber) {
        this.telephone = this.cloneBeneficiary.telephone;
      } else {
        this.telephone = '';
        this.dateOfBirth = undefined;
      }
      this.dateSigned = this.cloneBeneficiary.dateSigned;
      this.updateUserId = this.cloneBeneficiary.updateUserId;

      if (this.cloneBeneficiary.trusteeFirstName + this.cloneBeneficiary.trusteeLastName !== '') {
        this.beneficiaryUnder18 = 'Y';
      } else if (
        this.isCanadaPolicy &&
        (this.beneficiaryType === 'Y' || this.beneficiaryType === 'C')
      ) {
        this.beneficiaryUnder18 = 'N';
      } else {
        this.beneficiaryUnder18 = '';
      }
    }

    this.irrevocable = this.cloneBeneficiary?.irrevocable;
    this.revocable = !this.cloneBeneficiary?.irrevocable;

    // http://confluence.primerica.com/display/PD/Add+Beneficiary+Details
    // Beneficiary Type to Relationship Mapping - Backend Saving to the table

    // Only after the "Submit" from My Beneficiary do we actually save the data.
    window.scrollTo(0, 0);
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.moveRedStartBeforeLabel {
  margin-bottom: -15px;
  display: block;
  margin-left: -8px;
  padding-top: 15px;
}

.moveRedStarLeftOnly {
  margin-left: -5px;
}

.moveRedStarAfterFullname {
  position: relative;
  margin-top: -75px;
  margin-left: 80px;
  display: block;
  margin-bottom: 50px;
}

.gray-background {
  background-color: #f5f5f5;
}

.max-width-card {
  max-width: 360px;
}

.margin-left-20 {
  margin-left: 20px;
}

.margin-bottom-40 {
  margin-bottom: 40px;
}

.margin-left-40 {
  margin-left: 40px;
}

.input-label-header {
  color: #666666;
  font-size: 12px;
  font-weight: 600;
}

.redText {
  color: red;
}

.paddingLRMobile {
  padding-left: 10px;
  padding-right: 10px;
}

.paddingLRDesktop {
  padding-left: 20px;
  padding-right: 20px;
}

.move-right {
  float: right;
}

.bkgrnd-color-white {
  background-color: #ffffff;
}

.bkgrnd-color-blue-white {
  background-color: #f2f2f2;
}

.blue-bkgrnd {
  background-color: #c9e5f9;
}

.buttonFormat {
  width: 300px;
  height: 44px;
  font-size: 18px;
  font-weight: 600;
}

.sm-pl-5 {
  @media (min-width: 600px) {
    padding-left: 40px;
  }
}

@media (min-width: 768px) {
  .pad-left-md {
    padding-left: 12px;
  }

  .pad-right-md {
    padding-right: 12px;
  }
}

.blue-border {
  border: solid 2px #2196f3;
  cursor: pointer;
}

.select-address {
  color: #2196f3 !important;
}

:deep(label.v-label--active) {
  font-weight: 500;
  color: #333;
}

:deep(div.v-input) {
  padding-bottom: 12px;
}

h3.heading_three.font-weight-medium {
  font-weight: 500 !important;
}

.font-size-md {
  font-size: 16px !important;
}

button.short {
  height: 40px !important;
  text-transform: none !important;
  box-shadow: none !important;
  border: 1px #063562 solid;
  width: auto !important;
}

.tinyPixel {
  color: transparent;
  font-size: 0.001px;
  height: 0.001px;
}

.beneficiary-card.v-card {
  height: 100%;
  position: relative;
}

@media (max-width: 440px) {
  .beneficiary-card.v-card {
    width: 100vw;
    left: -1rem;
  }
}
</style>
